// Globals
import Vue from 'vue';
import Router from 'vue-router';
import store from '@/stores/index';
import { ACCOUNT, APP_STORE, LOCATION } from '@/stores/defs';

import customerService from '@/services/customer.service';
import localStorageAdapter from '@/utils/LocalStorageAdapter';
import { hasNewCustomerName } from '@/utils/helper.functions';

// Components
import MenuItemList from '@/components/menu/Adapter_MenuItemList';
import EventBus from '@EventBus';
import { has } from 'lodash';
import Checkout from '@/components/checkout/Checkout';
import CheckoutContainer from '@/components/checkout/CheckoutContainer';
import OrderConfirmation from '@/components/checkout/OrderConfirmation';
import OrderConfirmationMobile from '@/components/order-confirmation/OrderConfirmationMobile';
import OrderContainer from '@/components/orders/OrderContainer';
import PaymentResponse from '@/components/checkout/payment/PaymentResponse';
import PaymentMethod from '@/components/checkout/payment/PaymentMethod';
import Account from '@/components/account/Account';
import SignIn from '@/components/account/SignIn';
import SignUp from '@/components/account/SignUp';
import GuestForm from '@/components/account/GuestForm';
import EnterPin from '@/components/account/EnterPin';
// TODO remove this in favor of generic adapter
import MenuCategories from '@/components/menu/Adapter_MenuCategories';
import MenuItemDetailRoute from '@/components/menu/MenuItemDetailRoute';
import Login from '@/components/auth/Login';
import Logout from '@/components/auth/Logout';
import InjectableHeader from '@/components/header/InjectableHeader/InjectableHeader';
import StoreDeepLink from '@/components/store-locator/StoreDeepLink';
import NotFound from '@/components/NotFound';
import LandingPage from '@/components/LandingPage';
import ContentModal from '@/components/global-modal/ContentModal';
import LinkUser from '@/components/auth/LinkUser';
import _Adapter from '@/components/_Adapter';
import AppConstants from '@/utils/AppConstants';

const responsiveMobileCheck = () => {
	const screenWidth = window.innerWidth;
	const mobileWidth = (screenWidth < 1088);
	if (mobileWidth) {
		return true;
	}
	return false;
};

const ga = window.ga;

const USE_MOCK_LOGIN = (location.search.indexOf('use_mocks') !== -1);

Vue.use(Router);

const router = new Router({
	scrollBehavior: (to, from, savedPosition) => {
		if (savedPosition) {
			return (savedPosition);
		}
		return ({ x: 0, y: 0 });
	},
	mode: 'history',
	routes: [
		{
			path: '/',
			name: 'greeting',
			component: _Adapter,
			props: {
				adapterName: 'Greeting',
				path: 'landing',
			},
			meta: {
				transition: 'horizontal',
				priority: 0,
			},
			beforeEnter: (to, from, next) => {
				if (!responsiveMobileCheck()) {
					next('/home');
					return;
				}
				next();
			},
		},
		{
			path: '/service',
			name: 'service',
			props: {
				adapterName: 'ServiceChooser',
				path: 'choosers',
			},
			component: _Adapter,
			meta: {
				transition: 'horizontal',
				priority: 1,
			},
			beforeEnter: (to, from, next) => {
				const serviceActions = AppConstants.layoutOptions.service.actions;
				if (!responsiveMobileCheck()) {
					next('/home');
					return;
				}
				if (serviceActions.length === 0) {
					next('/locator');
					return;
				}
				if (!from.name) {
					next('/');
					return;
				}
				next();
			},
		},
		{
			path: '/locator',
			name: 'locator',
			props: {
				adapterName: 'StoreLocator',
				path: 'store-locator',
			},
			component: _Adapter,
			meta: {
				transition: 'horizontal',
				priority: 2,
			},
			beforeEnter: (to, from, next) => {
				if (from.name === 'home' || from.name === 'checkout') {
					next();
					return;
				}
				const isStoreDeepLink = store.getters[APP_STORE.GET_STORE_DEEP_LINK] || false;
				if (!responsiveMobileCheck() || isStoreDeepLink) {
					store.commit(APP_STORE.SET_STORE_DEEP_LINK, false);
					next('/home');
					return;
				}
				if (!from.name) {
					next('/');
					return;
				}
				next();
			},
		},
		{
			path: '/home',
			name: 'home',
			component: LandingPage,
			beforeEnter: (to, from, next) => {
				const isDebug = store.getters[APP_STORE.GET_DEBUG];
				if (isDebug) {
					const query = to.query || {};
					query.debug = 1;
					next({ query });
				}
				next();
			},
			meta: {
				transition: 'fade',
				priority: 3,
			},
		},
		{
			path: '/login',
			name: 'login',
			component: Login,
		},
		{
			path: '/logout',
			name: 'logout',
			component: Logout,
		},
		{
			path: '/menu',
			name: 'menu',
			component: MenuCategories,
		},
		{
			path: '/menu/:categoryId',
			name: 'category',
			component: MenuItemList,
			props: true,
		},
		{
			path: '/menu/:categoryId/:name',
			name: 'item-detail',
			props: true,
			component: MenuItemDetailRoute,
			meta: {
				transition: 'fade',
			},
		},
		{
			path: '/checkout',
			component: CheckoutContainer,
			children: [
				{
					path: '',
					name: 'checkout',
					component: Checkout,
					meta: {
						isCheckout: true,
						transition: 'horizontal',
						priority: 4,
					},
					beforeEnter(to, from, next) {
						const customer = store.getters[ACCOUNT.GET_CUSTOMER];
						let location = localStorageAdapter.getItem('location');
						if (!location) {
							location = store.getters[LOCATION.GET_LOCATION];
						}
						if (!location || !location.id) {
							next('/');
							return;
						}
						if (from && from.name === null) {
							next('/home');
							return;
						}
						store.commit(LOCATION.SET_LOCATION, location);
						const isDesktop = !responsiveMobileCheck();
						if (customer && customer.id && isDesktop) {
							const triggerModal = (component, title, props, modalName = 'contentModal') => {
								router.app.$modal.show(ContentModal, {
									contentComponent: component,
									contentProperties: props,
									header: {
										title,
										showBackButton: false,
										showCloseButton: true,
									},
									modalName,
									footer: {
										showFooter: false,
									},
								}, {
									name: modalName,
									width: (router.app.$mq === 'mobile') ? '100%' : '65%',
									height: 'auto',
									scrollable: true,
									clickToClose: false,
									classes: 'global-modal',
								});
							};
							const noName = !(customer.firstName || customer.lastName);
							const invalidName = hasNewCustomerName(customer.firstName, customer.lastName);
							if (noName || !customer.email || invalidName) {
								triggerModal(LinkUser, '', { routeTo: to.path });
							}
						} else {
							next();
						}
						next();
					},
				},
			],
		},
		{
			path: '/order',
			name: 'order',
			component: OrderContainer,
			children: [
				{
					path: 'order-confirmation',
					name: 'order-confirmation',
					component: OrderConfirmation,
					props: { default: true, checkoutOrder: null },
					meta: {
						transition: 'enter',
						priority: 6,
					},
				},
				{
					path: 'order-confirmation-mobile',
					name: 'order-confirmation-mobile',
					component: OrderConfirmationMobile,
					props: { default: true, checkoutOrder: null },
					meta: {
						transition: 'enter',
						priority: 6,
					},
				},
			],
		},
		{
			path: '/injectable-header',
			name: 'injectable-header',
			component: InjectableHeader,
		},
		{
			path: '/payment-method',
			name: 'payment-method',
			component: PaymentMethod,
			meta: {
				defaultBack: true,
				transition: 'horizontal',
				priority: 5,
			},
		},
		{
			path: '/payment-response',
			name: 'payment-response',
			component: PaymentResponse,
			meta: { hasNav: false },
		},
		{
			path: '/account/',
			name: 'account',
			component: Account,
			meta: { defaultBack: true },
			children: [
				{
					path: 'sign-in',
					name: 'account-sign-in',
					component: SignIn,
				},
				{
					path: 'sign-up',
					name: 'account-sign-up',
					component: SignUp,
					meta: { defaultBack: true },
				},
				{
					path: 'guest-form',
					name: 'account-guest-form',
					component: GuestForm,
					meta: { defaultBack: true },
				},
				{
					path: 'enter-pin',
					name: 'account-enter-pin',
					component: EnterPin,
					meta: { defaultBack: true },
				},
			],
		},
		{
			path: '/store/:handle',
			name: 'store-handle',
			component: StoreDeepLink,
			props: true,
		},
		{
			path: '/store/:handle/menu/:category/:itemName',
			name: 'store-category-item',
			component: StoreDeepLink,
			props: true,
		},
		{
			path: '/store/:handle/:itemId',
			name: 'store-item',
			component: StoreDeepLink,
			props: true,
		},
		{
			path: '/not-found',
			alias: '*',
			name: 'not-found',
			component: NotFound,
		},
	],
});

Object.defineProperty(Router.prototype, '$hasPreviousRoute', {
	value: (route) => {
		const history = route.history;
		const currentRoute = history.current;
		const routeMeta = currentRoute.meta;
		const defaultBack = routeMeta.defaultBack;
		if (defaultBack) {
			return true;
		}
		const currentParams = currentRoute.params;
		const previousMeta = currentParams.meta;
		if (!previousMeta) {
			return false;
		}
		const previousRoute = previousMeta.route;
		if (!previousRoute || previousRoute.name === 'store') {
			return false;
		}
		return true;
	},
});

Object.defineProperty(Router.prototype, '$transition', {
	get: () => {
		const routeMeta = router.currentRoute.meta;
		if (has(routeMeta, 'transition')) {
			return routeMeta.transition;
		}
		return null;
	},
});

Object.defineProperty(Router.prototype, '$getTransition', {
	value: (route) => {
		const routeMeta = route.meta;
		if (!routeMeta) {
			return null;
		}
		if (has(routeMeta, 'transition')) {
			return routeMeta;
		}
		return null;
	},
});

router.afterEach((to, from) => {
	EventBus.$emit('recalculate-header');
	if (USE_MOCK_LOGIN)  {
		store.dispatch(ACCOUNT.SET_LOGGED_IN, true);
		return;
	}
	if (from.name === to.name) {
		return;
	}
	const customer = store.getters[ACCOUNT.GET_CUSTOMER];
	const getCustomerId = new Promise((resolve) => {
		const storedCustomerBase = localStorageAdapter.getCustomerBase();
		const customerId = (customer) ? customer.id : null;
		resolve(customerId || storedCustomerBase || '');
	});

	getCustomerId.then((customerId) => {
		customerService.verifyLoginStatus(customerId).then((res) => {
			store.dispatch(ACCOUNT.SET_LOGGED_IN, res.data.body.loggedIn);
			ga((tracker) => {
				const currentUser = tracker.get('userId');
				if (currentUser !== customerId) {
					ga('set', 'userId', customerId);
					ga('send', 'userId');
					ga('set', 'dimension1', customerId);
				}
			});
		}).catch((err) => {
			console.log(err);
		});
	});

	if (ga) {
		ga('set', 'page', to.name);
		ga('send', 'pageview');
		if (to.params.item) {
			const item = to.params.item;
			ga('send', 'event', {
				eventCategory: 'Menu',
				eventAction: 'View',
				eventLabel: item.name,
			});
		}
	}
});

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js